import { useState } from "react";
import styles from "./LoginForm.module.css";
import { User, Lock, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { login, fetchUserProfile } from "../../services/api";
import T from '../T';

const LoginForm = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      toast.error("Please enter both username and password");
      return;
    }

    setIsLoading(true);
    try {
      const response = await login(credentials.username, credentials.password);
      const token = response.access_token;
      const refreshToken = response.refresh_token;

      // Fetch full profile
      const profile = await fetchUserProfile(token);

      // Store everything
      localStorage.setItem("staff_token", token);
      localStorage.setItem("staff_refresh_token", refreshToken);
      localStorage.setItem("staff_profile", JSON.stringify(profile));

      toast.success(`Welcome back, ${profile.firstname}!`);

      // Redirect to staff dashboard
      setTimeout(() => {
        navigate("/staff/dashboard");
      }, 1000);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Login error:", error);
      }
      toast.error(
        error instanceof Error
          ? error.message
          : "Login failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}><T>Login</T></h2>
        <p className={styles.subtitle}>
          <T>Enter your credentials to access the staff portal</T>.
        </p>
      </div>

      <form className={styles.form} onSubmit={handleSubmit}>
        {/* Username */}
        <div className={styles.inputGroup}>
          <div className={styles.iconWrapper}>
            <User size={16} />
          </div>
          <input
            type="email"
            name="username"
            value={credentials.username}
            onChange={handleChange}
            placeholder="your.name@autochek.africa"
            className={styles.input}
            required
            disabled={isLoading}
          />
        </div>

        {/* Password */}
        <div className={styles.inputGroup}>
          <div className={styles.iconWrapper}>
            <Lock size={16} />
          </div>
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            value={credentials.password}
            onChange={handleChange}
            placeholder="••••••••"
            className={styles.input}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            className={styles.eyeToggle}
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        <button
          type="submit"
          className={styles.signInButton}
          disabled={isLoading}
        >
          {isLoading ? (
            <T>Logging in...</T>
          ) : (
            <>
              <T>Login</T> <span className={styles.arrow}>→</span>
            </>
          )}
        </button>
 <div
          style={{
            textAlign: "right",
            marginTop: "-4px",
          }}
        >
          <span
            onClick={() => navigate("/forgot-password")}
            style={{
              color: "#f5a623",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "0.85rem",
            }}
          >
            <T>Forgot Password?</T>
          </span>
        </div>
        <div
          style={{
            marginTop: "1rem",
            textAlign: "center",
            fontSize: "0.9rem",
            color: "#6b7280",
          }}
        >
          <T>Don't have an account? Sign up</T>
          <span
            onClick={() => navigate("/signup")}
            style={{ color: "#f5a623", cursor: "pointer", fontWeight: "500" }}
          >
            {/* The "Sign up" link text can be handled inside the T above, or kept separate. But since they want precise translation for "Don't have an account? Sign up", it's better to just translate the whole thing or keep it separate if needed. The provided translation string is "Don't have an account? Sign up", so we'll adjust the span appropriately to just say "Sign up" in French if needed, but the dictionary has the full string. I will just use `<T>Don't have an account? Sign up</T>` for the whole text and let the span wrap the "Sign up" part... actually, let's keep it simple: */}
            <T>Sign up</T>
          </span>
        </div>

      </form>
    </div>
  );
};

export default LoginForm;
